// ============================================================
// Datei: src/components/shiftOverview/ShiftOverview.jsx
//
// Zweck:
// - Desktop Deployment-Übersicht für verfügbare öffentliche Schichten
// - zeigt Ressorts als Zeilen
// - zeigt Freitag / Samstag / Sonntag als Spalten
// - Helfer können sich direkt für öffentliche Schichten einschreiben
//
// ÄNDERUNGEN:
// - Backstagebetreuung wird zweizeilig angezeigt:
//   oben: "Backstage"
//   unten: "Betreuung"
// - Backstagebetreuung verwendet cssKey "Programm"
// - Kanal K verwendet cssKey "Programm"
// - Fotograf:innen verwendet cssKey "Kommunikation"
// - Awareness als eigenes Ressort ergänzt
// - Awareness/Aweareness wird robust erkannt
// - dadurch greifen die neuen CSS-Farben aus shiftOverview.css
// ============================================================

import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Api from "../../Api";
import RessortDescriptionModal from "../modals/RessortDescriptionModal";
import "./shiftOverview.css";

const MAX_ASSIGNED_SHIFTS = 6;

// ============================================================
// Reihenfolge der Tage für die Zeitkollisions-Prüfung
// ============================================================
const DAY_INDEX = {
	Freitag: 0,
	Samstag: 1,
	Sonntag: 2
};

// ============================================================
// Chai-Untergruppen anhand der shift_id
// ============================================================
const CHAI_GROUPS = {
	KUCHEN_KAFFEE: {
		id: "Chai: Kuchen & Kaffee",
		minShiftId: 301,
		maxShiftId: 307
	},
	CREPES: {
		id: "Chai: Crêpes",
		minShiftId: 311,
		maxShiftId: 317
	}
};

// ============================================================
// Programm-Untergruppen anhand der shift_id
//
// Wichtig:
// - Backstagebetreuung: 900–919
// - Kanal K:             920–949
// ============================================================
const PROGRAMM_GROUPS = {
	BACKSTAGEBETREUUNG: {
		id: "Backstagebetreuung",
		label: "Backstagebetreuung",
		labelPrefix: "Backstage",
		labelMain: "Betreuung",
		cssKey: "Programm",
		modalDept: "Programm",
		minShiftId: 900,
		maxShiftId: 919
	}

};

// ============================================================
// Fotograf:innen / Kommunikation anhand der shift_id
//
// Wichtig:
// - Kommunikation/Fotograf:innen-Schichten liegen bei 950–959.
// ============================================================
const FOTOGRAFEN_GROUP = {
	id: "Fotograf:innen",
	label: "Fotograf:innen",
	cssKey: "Kommunikation",
	modalDept: "Kommunikation",
	minShiftId: 950,
	maxShiftId: 959
};

// ============================================================
// Awareness
//
// Wichtig:
// - Im SQL-Kommentar steht teilweise "Aweareness".
// - Damit das Frontend auch bei einem Tippfehler im Department-Namen
//   stabil bleibt, akzeptieren wir beide Varianten.
// - Angezeigt wird immer sauber "Awareness".
// ============================================================
const AWARENESS_DEPARTMENT = {
	id: "Awareness",
	label: "Awareness",
	cssKey: "Awareness",
	modalDept: "Awareness",
	aliases: ["Awareness", "Aweareness"]
};

// ============================================================
// Ressorts / Zeilen der Deployment-Tabelle
// ============================================================
const FALLBACK_DEPARTMENTS = [
	{ id: "Bar", label: "Bar", cssKey: "Bar", modalDept: "Bar" },
	{ id: "Caipibar", label: "Caipibar", cssKey: "Caipibar", modalDept: "Caipibar" },

	{
		id: CHAI_GROUPS.KUCHEN_KAFFEE.id,
		label: "Chai: Kuchen & Kaffee",
		labelPrefix: "Chai:",
		labelMain: "Kuchen & Kaffee",
		cssKey: "Chai",
		modalDept: "Chai"
	},

	{
		id: CHAI_GROUPS.CREPES.id,
		label: "Chai: Crêpes",
		labelPrefix: "Chai:",
		labelMain: "Crêpes",
		cssKey: "Chai",
		modalDept: "Chai"
	},

	{
		id: "Food",
		label: "Food",
		cssKey: "Food",
		modalDept: "Food"
	},

	{ id: "Infostand", label: "Infostand", cssKey: "Infostand", modalDept: "Infostand" },

	{
		id: AWARENESS_DEPARTMENT.id,
		label: AWARENESS_DEPARTMENT.label,
		cssKey: AWARENESS_DEPARTMENT.cssKey,
		modalDept: AWARENESS_DEPARTMENT.modalDept
	},

	// ============================================================
	// Programm-Untergruppen
	// ============================================================
	{
		id: PROGRAMM_GROUPS.BACKSTAGEBETREUUNG.id,
		label: PROGRAMM_GROUPS.BACKSTAGEBETREUUNG.label,
		labelPrefix: PROGRAMM_GROUPS.BACKSTAGEBETREUUNG.labelPrefix,
		labelMain: PROGRAMM_GROUPS.BACKSTAGEBETREUUNG.labelMain,
		cssKey: PROGRAMM_GROUPS.BACKSTAGEBETREUUNG.cssKey,
		modalDept: PROGRAMM_GROUPS.BACKSTAGEBETREUUNG.modalDept
	},


	// ============================================================
	// Kommunikation-Untergruppe
	// ============================================================
	{
		id: FOTOGRAFEN_GROUP.id,
		label: FOTOGRAFEN_GROUP.label,
		cssKey: FOTOGRAFEN_GROUP.cssKey,
		modalDept: FOTOGRAFEN_GROUP.modalDept
	},

	{ id: "Kaffimobil", label: "Kaffimobil", cssKey: "Kaffimobil", modalDept: "Kaffimobil" },
	{ id: "Kasse", label: "Kasse", cssKey: "Kasse", modalDept: "Kasse" },
	{ id: "Kindernami", label: "Kindernami", cssKey: "Kindernami", modalDept: "Kindernami" },
	{ id: "Parkplatz", label: "Parkplatz", cssKey: "Parkplatz", modalDept: "Parkplatz" },
	{ id: "Shuttle", label: "Schüttelbus", cssKey: "Schttelbus", modalDept: "Shuttle" },

	{
		id: "UmweltAmbiente",
		label: "Umwelt & Ambiente",
		cssKey: "UmweltAmbiente",
		modalDept: "UmweltAmbiente"
	}
];

const ShiftOverview = ({ shifts = [], assignedShifts = [], handleJoinShift }) => {
	const [ressortDescriptions, setRessortDescriptions] = useState([]);
	const [activeDept, setActiveDept] = useState(null);

	// ============================================================
	// Ressortbeschreibung laden
	// ============================================================
	const handleInfoClick = async (dept) => {
		try {
			const descriptionDepartment = dept.modalDept || dept.id;

			const response = await Api.get(
				`/departments/${descriptionDepartment}/descriptions`
			);

			setRessortDescriptions(response.data || []);
			setActiveDept(descriptionDepartment);
		} catch (error) {
			console.error("Fehler beim Laden der Ressortbeschreibung", error);

			setRessortDescriptions([]);
			setActiveDept(dept.modalDept || dept.id);
		}
	};

	const closeDescriptionModal = () => {
		setActiveDept(null);
		setRessortDescriptions([]);
	};

	// ============================================================
	// Wandelt Tag + Uhrzeit in absolute Minuten um.
	// Wird gebraucht, um Zeitüberschneidungen zu erkennen.
	// ============================================================
	const toAbsoluteMinutes = (day, time) => {
		if (!time || !Object.prototype.hasOwnProperty.call(DAY_INDEX, day)) {
			return null;
		}

		const [h, m] = time.split(":").map(Number);
		return DAY_INDEX[day] * 1440 + h * 60 + m;
	};

	// ============================================================
	// Bereits zugewiesene Schichten als Zeitintervalle vorbereiten
	// ============================================================
	const assignedIntervals = useMemo(() => {
		return assignedShifts
			.map((shift) => {
				const start = toAbsoluteMinutes(
					shift.dayOfDeployment,
					shift.startTime
				);

				let end = toAbsoluteMinutes(
					shift.dayOfDeployment,
					shift.endTime
				);

				if (start === null || end === null) {
					return null;
				}

				// Schichten über Mitternacht korrekt behandeln
				if (end <= start) {
					end += 1440;
				}

				return { start, end };
			})
			.filter(Boolean);
	}, [assignedShifts]);

	// ============================================================
	// Prüft, ob sich eine auswählbare Schicht mit einer bereits
	// zugewiesenen Schicht überschneidet.
	// ============================================================
	const isTimeBlocked = (shift) => {
		const start = toAbsoluteMinutes(
			shift.dayOfDeployment,
			shift.startTime
		);

		let end = toAbsoluteMinutes(
			shift.dayOfDeployment,
			shift.endTime
		);

		if (start === null || end === null) {
			return false;
		}

		if (end <= start) {
			end += 1440;
		}

		return assignedIntervals.some(
			(assigned) => start < assigned.end && assigned.start < end
		);
	};

	// ============================================================
	// Prüft, ob eine shiftId innerhalb eines Bereichs liegt.
	// ============================================================
	const isShiftIdBetween = (shiftId, min, max) => {
		const numericShiftId = Number(shiftId);

		return numericShiftId >= min && numericShiftId <= max;
	};

	// ============================================================
	// Prüft, ob eine Schicht zu Awareness gehört.
	//
	// Hintergrund:
	// - Korrekt ist "Awareness".
	// - Falls in der Datenbank oder im DTO versehentlich "Aweareness"
	//   steht, wird die Schicht trotzdem angezeigt.
	// ============================================================
	const isAwarenessShift = (shift) => {
		const departmentName = shift.department || shift.departmentName || "";

		return AWARENESS_DEPARTMENT.aliases.includes(departmentName);
	};

	// ============================================================
	// Ordnet Schichten einer Zeile zu.
	//
	// Normale Ressorts:
	// - direkter Vergleich über shift.department
	//
	// Chai:
	// - Untergruppen werden über shiftId erkannt
	//
	// Programm:
	// - Backstagebetreuung: 900–919
	// - Kanal K: 920–949
	//
	// Kommunikation:
	// - Fotograf:innen: 950–959
	// Awareness:
	// - direkter Department-Vergleich, inkl. Alias "Aweareness"
	// ============================================================
	const belongsToDepartmentRow = (shift, deptId) => {
		if (!shift || !deptId) {
			return false;
		}

		// Programm: Backstagebetreuung
		if (deptId === PROGRAMM_GROUPS.BACKSTAGEBETREUUNG.id) {
			return isShiftIdBetween(
				shift.shiftId,
				PROGRAMM_GROUPS.BACKSTAGEBETREUUNG.minShiftId,
				PROGRAMM_GROUPS.BACKSTAGEBETREUUNG.maxShiftId
			);
		}


		// Kommunikation: Fotograf:innen
		if (deptId === FOTOGRAFEN_GROUP.id) {
			return isShiftIdBetween(
				shift.shiftId,
				FOTOGRAFEN_GROUP.minShiftId,
				FOTOGRAFEN_GROUP.maxShiftId
			);
		}

		// Awareness wird als normales Ressort angezeigt.
		// Zusätzlich wird "Aweareness" als Tippfehler akzeptiert.
		if (deptId === AWARENESS_DEPARTMENT.id) {
			return isAwarenessShift(shift);
		}

		// Programm und Kommunikation werden nicht als Sammelzeile verwendet,
		// da sie über Untergruppen angezeigt werden.
		if (
			shift.department === "Programm" ||
			shift.departmentName === "Programm" ||
			shift.department === "Kommunikation" ||
			shift.departmentName === "Kommunikation"
		) {
			return false;
		}

		// Normale Ressorts
		if (shift.department === deptId || shift.departmentName === deptId) {
			return true;
		}

		// Chai-Sonderlogik
		if (shift.department !== "Chai" && shift.departmentName !== "Chai") {
			return false;
		}

		if (deptId === CHAI_GROUPS.KUCHEN_KAFFEE.id) {
			return isShiftIdBetween(
				shift.shiftId,
				CHAI_GROUPS.KUCHEN_KAFFEE.minShiftId,
				CHAI_GROUPS.KUCHEN_KAFFEE.maxShiftId
			);
		}

		if (deptId === CHAI_GROUPS.CREPES.id) {
			return isShiftIdBetween(
				shift.shiftId,
				CHAI_GROUPS.CREPES.minShiftId,
				CHAI_GROUPS.CREPES.maxShiftId
			);
		}

		return false;
	};

	const maxShiftsReached = assignedShifts.length >= MAX_ASSIGNED_SHIFTS;
	const days = ["Freitag", "Samstag", "Sonntag"];

	const departments = useMemo(() => {
		return FALLBACK_DEPARTMENTS;
	}, []);

	// ============================================================
	// Öffentliche Schichten pro Zelle laden.
	//
	// Wichtig:
	// - Nur shiftVisibility === "PUBLIC" wird angezeigt.
	// ============================================================
	const getPublicShiftsForCell = (deptId, day) => {
		return shifts
			.filter(
				(shift) =>
					belongsToDepartmentRow(shift, deptId) &&
					shift.dayOfDeployment === day &&
					shift.shiftVisibility === "PUBLIC"
			)
			.sort(
				(a, b) =>
					toAbsoluteMinutes(a.dayOfDeployment, a.startTime) -
					toAbsoluteMinutes(b.dayOfDeployment, b.startTime)
			);
	};

	const hasNoPublishedShiftsAtAll = (deptId) => {
		return days.every(
			(day) => getPublicShiftsForCell(deptId, day).length === 0
		);
	};

	// ============================================================
	// Rendert den Namen im Ressort-Feld.
	// Wird für Chai und Backstagebetreuung zweizeilig genutzt.
	// ============================================================
	const renderDepartmentLabel = (dept) => {
		if (!dept.labelPrefix || !dept.labelMain) {
			return dept.label;
		}

		return (
			<span className="dept-label-text">
				<span className="dept-label-prefix">{dept.labelPrefix}</span>
				<span className="dept-label-main">{dept.labelMain}</span>
			</span>
		);
	};

	return (
		<div className="deployment-table">
			<div className="table-header">
				<div className="header-cell ressort">Ressort</div>
				<div className="header-cell freitag">Freitag</div>
				<div className="header-cell samstag">Samstag</div>
				<div className="header-cell sonntag">Sonntag</div>
			</div>

			{departments.map((dept) => {
				const showDummies = hasNoPublishedShiftsAtAll(dept.id);

				return (
					<div className="table-row" key={dept.id}>
						<div
							className={`dept-label dept-${dept.cssKey}`}
							onClick={() => handleInfoClick(dept)}
						>
							<img
								src={`${process.env.PUBLIC_URL}/icons/icon_info.svg`}
								className="dept-info-icon"
								alt="info"
								onClick={(event) => {
									event.stopPropagation();
									handleInfoClick(dept);
								}}
							/>

							{renderDepartmentLabel(dept)}
						</div>

						{days.map((day) => {
							const dayShifts = getPublicShiftsForCell(dept.id, day);

							return (
								<div className="shift-cell" key={`${dept.id}-${day}`}>
									{dayShifts.map((shift) => {
										const available = Math.max(
											0,
											shift.publicSlots ?? shift.availableSlots ?? 0
										);

										const isFull = available <= 0;

										const joinDisabled =
											isFull ||
											maxShiftsReached ||
											isTimeBlocked(shift);

										return (
											<div
												key={shift.shiftId}
												className={`shift-card
													${joinDisabled ? "disabled" : ""}
													${isFull ? "full" : ""}
													dept-${dept.cssKey}
													day-${day}`}
											>
												<div className="shift-info">
													<p>
														{shift.startTime} – {shift.endTime}
													</p>

													<p>
														{isFull
															? "Voll"
															: `Verfügbar: ${available}`}
													</p>

													<button
														className="join-button"
														type="button"
														onClick={() => handleJoinShift(shift.shiftId)}
														disabled={joinDisabled}
													>
														{isFull ? "Voll" : "Einschreiben"}
													</button>
												</div>
											</div>
										);
									})}

									{showDummies && day === "Freitag" && (
										<div
											className={`shift-card shift-card-dummy dept-${dept.cssKey}`}
										>
											<div className="shift-info">
												<p>Zurzeit stehen keine</p>
												<p>Schichten zur Auswahl</p>
											</div>
										</div>
									)}
								</div>
							);
						})}
					</div>
				);
			})}

			{activeDept &&
				createPortal(
					<RessortDescriptionModal
						department={activeDept}
						descriptions={ressortDescriptions}
						onClose={closeDescriptionModal}
					/>,
					document.body
				)}
		</div>
	);
};

export default ShiftOverview;